import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LangContext";
import MotionReveal from "@/components/MotionReveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { Feather, Play, Pause } from "lucide-react";
import VoiceRecorder from "@/components/VoiceRecorder";

const MAX_MESSAGE = 1200;
const MAX_NAME = 80;

const langToLocale = (l) =>
  ({ en: "en-GB", fr: "fr-FR", el: "el-GR", pl: "pl-PL" }[l] || "en-GB");

const formatDate = (iso, lang) => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(langToLocale(lang), {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

const fmtDuration = (ms) => {
  if (!ms) return "";
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/** Small inline player for entries that carry a voice_url. */
const EntryAudio = ({ url, durationMs }) => {
  const [playing, setPlaying] = React.useState(false);
  const ref = React.useRef(null);
  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  };
  return (
    <div className="mt-4 inline-flex items-center gap-3 rounded-full bg-foreground/[0.04] border border-border px-3 py-2">
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause" : "Play"}
        data-testid="guestbook-entry-play"
        className="w-9 h-9 rounded-full bg-foreground text-background inline-flex items-center justify-center hover:bg-foreground/90"
      >
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button>
      <span className="text-[11px] tracking-[0.14em] uppercase text-foreground/60">
        Voice note{durationMs ? ` · ${fmtDuration(durationMs)}` : ""}
      </span>
      <audio
        ref={ref}
        src={url}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        preload="metadata"
        className="hidden"
      />
    </div>
  );
};

const Guestbook = () => {
  const { t, lang } = useLang();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [voice, setVoice] = useState(null); // { url, duration_ms } | null
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/guestbook");
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remaining = MAX_MESSAGE - message.length;

  const onSubmit = async (e) => {
    e.preventDefault();
    const cleanName = name.trim();
    const cleanMsg = message.trim();
    if (!cleanName) {
      toast.error(t("guestbook.errorEmpty") || "Name required.");
      return;
    }
    if (!cleanMsg && !voice) {
      toast.error(t("guestbook.errorNoContent") || "Add a message or record a voice note.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: cleanName.slice(0, MAX_NAME),
        message: cleanMsg.slice(0, MAX_MESSAGE),
        language: lang,
        voice_url: voice?.url || "",
        voice_duration_ms: voice?.duration_ms || 0,
      };
      const res = await api.post("/guestbook", payload);
      setEntries((prev) => [res.data, ...prev]);
      setMessage("");
      setVoice(null);
      toast.success(t("guestbook.successToast"));
    } catch {
      toast.error(t("guestbook.errorGeneric"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="guestbook-page">
      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 pt-24 sm:pt-32">
        <MotionReveal>
          <p className="editorial-kicker">{t("guestbook.overline")}</p>
        </MotionReveal>
        <MotionReveal delay={0.05}>
          <h1 className="editorial-h1 mt-6 text-4xl sm:text-6xl lg:text-7xl max-w-[18ch]">
            {t("guestbook.title")}
          </h1>
        </MotionReveal>
        <MotionReveal delay={0.1}>
          <p className="mt-8 text-foreground/75 max-w-[56ch] leading-relaxed text-lg">
            {t("guestbook.intro")}
          </p>
        </MotionReveal>
      </section>

      {/* Compose */}
      <section
        className="mx-auto max-w-[820px] px-5 sm:px-8 mt-14 sm:mt-20"
        data-testid="guestbook-form-section"
      >
        <form onSubmit={onSubmit} className="surface-card p-6 sm:p-8 space-y-6" data-testid="guestbook-form">
          <div className="space-y-2">
            <Label htmlFor="gb-name" className="editorial-kicker">
              {t("guestbook.nameLabel")}
            </Label>
            <Input
              id="gb-name"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, MAX_NAME))}
              required
              maxLength={MAX_NAME}
              placeholder={t("guestbook.namePlaceholder")}
              data-testid="guestbook-name-input"
              className="h-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gb-message" className="editorial-kicker">
              {t("guestbook.messageLabel")}
            </Label>
            <Textarea
              id="gb-message"
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
              maxLength={MAX_MESSAGE}
              placeholder={t("guestbook.messagePlaceholder")}
              data-testid="guestbook-message-input"
              className="border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent resize-none"
            />
            <p className="editorial-kicker text-foreground/50" data-testid="guestbook-counter">
              {remaining} {t("guestbook.counter")}
            </p>
          </div>

          {/* Voice note */}
          <VoiceRecorder onUploaded={setVoice} disabled={busy} />

          <div className="pt-2">
            <Button
              type="submit"
              disabled={busy}
              data-testid="guestbook-submit-button"
              className="h-12 px-8 rounded-xl bg-foreground text-background hover:bg-foreground/90 tracking-[0.22em] uppercase text-[12px] gap-2"
            >
              <Feather className="w-4 h-4" aria-hidden="true" />
              {busy ? t("guestbook.sending") : t("guestbook.submit")}
            </Button>
          </div>
        </form>
      </section>

      {/* Entries */}
      <section
        className="mx-auto max-w-[820px] px-5 sm:px-8 mt-14 sm:mt-20 pb-24"
        data-testid="guestbook-entries-section"
      >
        {loading ? (
          <div className="editorial-kicker text-center py-16" data-testid="guestbook-loading">
            · · ·
          </div>
        ) : entries.length === 0 ? (
          <div
            className="surface-card p-10 text-center"
            data-testid="guestbook-empty"
          >
            <p className="editorial-kicker">{t("guestbook.emptyTitle")}</p>
            <p className="mt-3 font-serif-display italic text-2xl">{t("guestbook.emptyBody")}</p>
          </div>
        ) : (
          <ul className="space-y-6 sm:space-y-8" data-testid="guestbook-list">
            {entries.map((it, i) => (
              <motion.li
                key={it.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i, 6) * 0.04, ease: [0.2, 0.8, 0.2, 1] }}
                data-testid={`guestbook-entry-${it.id}`}
                className="surface-card p-6 sm:p-8"
              >
                <p className="editorial-kicker text-accent">
                  {formatDate(it.created_at, lang)}
                </p>
                {it.message && (
                  <p className="mt-4 font-serif-display italic text-2xl sm:text-3xl leading-snug whitespace-pre-wrap">
                    {it.message}
                  </p>
                )}
                {it.voice_url && (
                  <EntryAudio url={it.voice_url} durationMs={it.voice_duration_ms} />
                )}
                <p className="mt-6 text-[12px] tracking-[0.22em] uppercase text-foreground/60">
                  — {it.name}
                </p>
              </motion.li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default Guestbook;
